/* eslint-disable react/prop-types */

import React from 'react';
import { Card, CardFooter, CardBody, Row, Col } from 'reactstrap';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { Link, withRouter } from 'react-router-dom';
import { Field, FormAlerts, Button, useFeatures } from '@cordata/buffet';

import {
  adminInstance as axios,
  toastSuccess,
} from '~/utils';

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .required(),
    sortOrder: Yup.mixed()
      .required(),
    identifier: Yup.string()
      .required(),
    status: Yup.string()
      .required(),
    startDate: Yup.date()
      .required(),
    endDate: Yup.date(),
  }),

  mapPropsToValues: props => ({
    id: props.data && props.data.id,
    name: props.data && props.data.attributes.clinicalTrialName,
    identifier: props.data && props.data.attributes.clinicalTrialId,
    type: props.data && props.data.attributes.clinicalTrialType && {
      value: props.data.attributes.clinicalTrialType,
      label: props.data.attributes.clinicalTrialType,
    },
    phase: props.data && props.data.attributes.clinicalTrialPhase && {
      value: props.data.attributes.clinicalTrialPhase,
      label: props.data.attributes.clinicalTrialPhase,
    },
    eligibility: props.data && props.data.attributes.eligibilityCriteria,
    status: props.data && props.data.attributes.status && {
      value: props.data.attributes.status,
      label: props.data.attributes.status,
    },
    principalInvestigator: props.data && props.data.attributes.principalInvestigator,
    description: props.data && props.data.attributes.clinicalTrialDescription,
    startDate: (props.data && props.data.attributes.clinicalTrialStartDate)
      ? new Date(props.data.attributes.clinicalTrialStartDate)
      : undefined,
    endDate: (props.data && props.data.attributes.clinicalTrialEndDate)
      ? new Date(props.data.attributes.clinicalTrialEndDate)
      : undefined,
    sortOrder: props.data && props.data.attributes.sortOrder,
  }),

  handleSubmit: (values, { setErrors, setSubmitting, ...bag }) => {
    const { props: { history } } = bag;

    const p = {
      clinicalTrialName: values.name,
      clinicalTrialLabel: values.name,
      clinicalTrialId: values.identifier,
      clinicalTrialType: values.type && values.type.value,
      clinicalTrialPhase: values.phase && values.phase.value,
      clinicalTrialDescription: values.description,
      clinicalTrialStartDate: values.startDate,
      clinicalTrialEndDate: values.endDate,
      eligibilityCritera: values.eligibility,
      principalInvestigator: values.principalInvestigator,
      sortOrder: values.sortOrder,
      status: values.status && values.status.value,
      orgUid: localStorage.getItem('org'),
      divisionUid: localStorage.getItem('div'),
    };

    if (!values.id) {
      axios
        .post('/clinical-trial', p)
        .then(() => toastSuccess())
        .then(() => history.push('/admin/clinical-trials'))
        .catch(e => setErrors({ server: e.errors }))
        .then(() => setSubmitting(false));
    } else {
      axios
        .put(`/clinical-trial/${values.id}`, p)
        .then(() => toastSuccess())
        .then(() => history.push('/admin/clinical-trials'))
        .catch(e => setErrors({ server: e.errors }))
        .then(() => setSubmitting(false));
    }
  },

  displayName: 'ClinicalTrialsForm',
  validateOnChange: false,
  validateOnBlur: false,
});

const clinicalTrialTypes = [
  'Observational',
  'Treatment',
  'Prevention',
  'Screening',
  'Quality of Life',
  'Genetics',
].map(r => ({ label: r, value: r }));

const clinicalTrialPhases = [
  'Phase 0',
  'Phase I',
  'Phase II',
  'Phase III',
  'Phase IV',
].map(r => ({ label: r, value: r }));

const clinicalTrialStatuses = [
  'Open - accruing',
  'Closed - suspended',
  'Closed',
].map(r => ({ label: r, value: r }));

const ClinicalTrialsForm = ({
  errors,
  handleSubmit,
  isSubmitting,
  submitCount,
}) => {
  const { hasAppKickstarter } = useFeatures();

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        <CardBody>
          { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

          <Row form>
            <Col sm>
              <Field
                name="name"
                required
              />
            </Col>

            <Col sm>
              <Field
                name="sortOrder"
                type="number"
                required
              />
            </Col>

            <Col sm>
              <Field
                name="identifier"
                required
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="type"
                type="select"
                options={clinicalTrialTypes}
              />
            </Col>

            <Col sm>
              <Field
                name="phase"
                type="select"
                options={clinicalTrialPhases}
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="eligibility"
              />
            </Col>

            <Col sm>
              <Field
                name="status"
                type="select"
                options={clinicalTrialStatuses}
                required
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="principalInvestigator"
                type="textarea"
              />
            </Col>

            <Col sm>
              <Field
                name="description"
                type="textarea"
              />
            </Col>
          </Row>

          <Row form>
            <Col sm>
              <Field
                name="startDate"
                type="date"
                required
              />
            </Col>

            <Col sm>
              <Field
                name="endDate"
                type="date"
              />
            </Col>
          </Row>

          {hasAppKickstarter && (
            <Field
              name="id"
              label="Clinical Trial Uid"
              placeholder=" "
              disabled
            />
          )}
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="cancel"
              disabled={isSubmitting}
              tag={Link}
              to="/admin/clinical-trials"
            >
              Cancel
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};

export default withRouter(formEnhancer(ClinicalTrialsForm));
